// Selectors that take into account the state in which the user is in the UI (e.g. which subflow they are in).
// Also combine multiple lower level selectors like bundle selectors and seatmap selectors.

import { createSelector, MemoizedSelector } from '@ngrx/store';

import { Category, FinnairServiceVariantItemV3 } from '@fcom/dapi/api/models';
import {
  CommonBookingFeatureState,
  OrderStatus,
  seatBundleCatalogServicesByFlightAndPassengerSelector,
  seatBundleCategoriesToSubCategoriesMappingSelector,
  seatBundleSelectionsByFlightAndPassengerSelector,
} from '@fcom/common-booking/store';
import { selectCommonBookingState } from '@fcom/common-booking/store/selectors/feature.selector';

/**
 * Which passengers have seat selections that should be removable in the current UI.
 * For example for displaying remove buttons in the seatmap passenger chips.
 */
export const selectEnableRemovingSeatSelection = (props: {
  contextCategory: Category;
  flightId: string;
}): MemoizedSelector<CommonBookingFeatureState, Record<string, boolean>> =>
  createSelector(
    selectCommonBookingState,
    seatBundleCategoriesToSubCategoriesMappingSelector,
    seatBundleSelectionsByFlightAndPassengerSelector,
    (state, matchingBundleSubCategories, bundleSelectionsBy) => {
      const seatmapSelectionsByPax = state.seatMap?.[props.flightId]?.selections || {};
      const passengersWithSelections = new Set([
        ...Object.keys(seatmapSelectionsByPax),
        ...Object.keys(bundleSelectionsBy[props.flightId] || {}),
      ]);
      return Array.from(passengersWithSelections).reduce((acc, curPaxId) => {
        const bundleSelections = bundleSelectionsBy[props.flightId]?.[curPaxId] || [];
        const seatSelection = seatmapSelectionsByPax[curPaxId];

        const allow = {
          ...acc,
          [curPaxId]: true,
        };
        const disallow = {
          ...acc,
          [curPaxId]: false,
        };

        if (state.order?.orderStatus !== OrderStatus.INITIAL) {
          return disallow;
        }
        if (bundleSelections.length === 0 && !seatSelection?.seatNumber) {
          return disallow;
        }
        if (
          bundleSelections.length > 0 &&
          bundleSelections.every(
            (bs) => matchingBundleSubCategories[props.contextCategory]?.has(bs.category) && bs.modifiable !== false
          )
        ) {
          return allow;
        } else if (bundleSelections.length > 0) {
          return disallow;
        }
        return seatSelection?.modifiable ? allow : disallow;
      }, {});
    }
  );

/**
 * Returns the FinnairServiceVariantItemV3[] that should be offered for a category+flight+passenger.
 */
export const matchingSeatBundleOfferServicesSelector = (
  category: Category,
  flightId: string,
  passengerId: string
): MemoizedSelector<CommonBookingFeatureState, FinnairServiceVariantItemV3[]> =>
  createSelector(
    seatBundleCatalogServicesByFlightAndPassengerSelector,
    seatBundleCategoriesToSubCategoriesMappingSelector,
    (bundleCatalog, bundleCategoryMapping) =>
      bundleCatalog[flightId]?.[passengerId]?.filter((i) => bundleCategoryMapping[category]?.has(i.subCategory)) || []
  );
